import React, { useCallback, useEffect } from "react";
import { CoachSettings, ShortLinkSource } from "@/interfaces";
import {
  PlayerCard,
  PlayerCardServices,
  PlayerShortLink,
  PlayersServices as BedrockPlayersServices,
  PlayersServices,
} from "@/services/bedrock";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { get } from "lodash";

import {
  manageWebAddress,
  noSpaces,
  removeSpecialCharacters,
  URLS,
  usePlayerSettings,
  useSocialCoachSession,
  webUrlRegExp,
} from "@/utils";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button, Field, Input, InputGroup, Card, Skeleton } from "@/components/ui";
import { cn } from "@/lib/utils";
import { TriangleAlert } from "lucide-react";
import { FormCard } from "@/components";

const shortLinkSchema = z
  .object({
    slug: z.string().trim(),
    webAddress: z.string().trim().optional(),
    shortLinkSource: z.enum(["PLAYER_CARD", "CUSTOM_URL"]).optional(),
  })
  .superRefine((data, ctx) => {
    // If shortLinkSource is "CUSTOM_URL" (or undefined/falsy) and they have a slug, then webAddress is required
    const isCustomUrl = !data.shortLinkSource || data.shortLinkSource === "CUSTOM_URL";
    if (isCustomUrl && data.slug && !data.webAddress) {
      ctx.addIssue({
        code: "custom",
        path: ["webAddress"],
        message: "Player's Website is required",
      });
    }

    // Also validate the webAddress URL if it's entered
    if (data.webAddress && data.webAddress !== "" && !webUrlRegExp.test(data.webAddress)) {
      ctx.addIssue({
        code: "custom",
        path: ["webAddress"],
        message: "Player's Website is invalid",
      });
    }
  });

type ShortLinkFormValues = z.infer<typeof shortLinkSchema>;

interface Props {
  playerId: string | number;
  playerShortLink?: PlayerShortLink;
  playerWebAddress?: string | null;
}

export function PlayerShortLinkForm({ playerId }: Props) {
  const { isPlayer } = useSocialCoachSession();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [shortLink, setShortLink] = React.useState<PlayerShortLink>();
  const [playerWebAddress, setPlayerWebAddress] = React.useState<string | null>();
  const shortLinkBaseUrl = shortLink?.url ? shortLink.url.split("/") : undefined;
  const shortLinkBase =
    "https://" + (shortLinkBaseUrl ? shortLinkBaseUrl[shortLinkBaseUrl.length - 2] : "myoc.io") + "/";
  const [playerCard, setPlayerCard] = React.useState<PlayerCard | null>();
  const [loadingPlayerCard, setLoadingPlayerCard] = React.useState<boolean>(false);
  const { hasSetting } = usePlayerSettings(playerId);
  const playerHasPlayerCardAccess = hasSetting(CoachSettings.ALLOW_PLAYER_ACCESS_TO_PLAYER_CARDS);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isSubmitting },
  } = useForm<ShortLinkFormValues>({
    resolver: zodResolver(shortLinkSchema),
    mode: "onTouched",
    defaultValues: {
      slug: "",
      webAddress: "",
      shortLinkSource: "CUSTOM_URL",
    },
  });

  const loadShortLink = useCallback(() => {
    if (!playerId) return;
    setLoading(true);
    BedrockPlayersServices.getPlayer(playerId, ["shortLink", "webAddress"])
      .then(bedrockPlayer => {
        setShortLink(bedrockPlayer.shortLink);
        setPlayerWebAddress(bedrockPlayer.webAddress);
      })
      .catch(e => {
        toast.error("Error loading short link: " + e.message || "Unknown error. Please try again later.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [playerId, setLoading, setShortLink, setPlayerWebAddress]);

  useEffect(() => {
    loadShortLink();
  }, [playerId]);

  useEffect(() => {
    if (playerHasPlayerCardAccess) {
      setLoadingPlayerCard(true);
      PlayerCardServices.getMainPlayerCard(playerId)
        .then(setPlayerCard)
        .finally(() => setLoadingPlayerCard(false));
    }
  }, [playerHasPlayerCardAccess]);

  // Sync form state when loaded data changes
  useEffect(() => {
    if (!loading) {
      reset({
        slug: get(shortLink, "slug") || "",
        webAddress: playerWebAddress || "",
        shortLinkSource: (get(shortLink, "source") as "PLAYER_CARD" | "CUSTOM_URL") || "CUSTOM_URL",
      });
    }
  }, [shortLink, playerWebAddress, loading, reset]);

  const updateShortLinkInfo = (
    slug?: string | null,
    webAddress?: string | null,
    source?: ShortLinkSource
  ): Promise<any> => {
    return PlayersServices.updatePlayer(playerId, {
      shortLink: {
        slug,
        source,
      },
      webAddress,
    });
  };

  const getTruncatedWebsiteLabel = (url: string | null | undefined) => {
    if (!url) return "";
    const maxLength = 25;
    if (url.length <= maxLength) return ` (${url})`;
    return ` (${url.substring(0, maxLength)}...)`;
  };

  const onSubmit = async (data: ShortLinkFormValues) => {
    try {
      const { slug, webAddress, shortLinkSource } = data;
      const webAddressToSave = webAddress?.endsWith("/") ? webAddress.slice(0, -1) : webAddress;
      const webAddressWithFormat = (await manageWebAddress(webAddressToSave || undefined)) || null;

      await updateShortLinkInfo(slug || null, webAddressWithFormat || null, shortLinkSource || "CUSTOM_URL");
      loadShortLink();
      toast.success("Short link updated successfully");
    } catch (e: any) {
      let errorMessage = e.message || e.toString();
      if (errorMessage.includes("Player link has already been taken")) {
        errorMessage = "Short link is already taken";
      }
      toast.error(errorMessage);
    }
  };

  const currentWebAddress = watch("webAddress");
  const currentShortLinkSource = watch("shortLinkSource");

  return (
    <FormCard>
      <FormCard.Header title="Short Link" description="Create a short, trackable link to share on social media" />
      <FormCard.Content>
        <form id="player-shortlink-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-6">
            <Controller
              name="webAddress"
              control={control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <Field.Label htmlFor="webAddress">Player's Website</Field.Label>
                  {loading ? (
                    <Skeleton className="h-9 w-full rounded-lg" />
                  ) : (
                    <Input
                      {...field}
                      id="webAddress"
                      placeholder="www.yourwebsite.com"
                      disabled={loading || isSubmitting}
                      onChange={e => {
                        field.onChange(noSpaces(e.currentTarget.value));
                      }}
                    />
                  )}
                  {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
                </Field>
              )}
            />

            <Controller
              name="slug"
              control={control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <div className="flex items-center justify-between">
                    <Field.Label htmlFor="slug">Short Code</Field.Label>
                    {!loading &&
                      (field.value ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => {
                            navigator.clipboard.writeText(shortLinkBase + field.value);
                            toast.success("Link copied to clipboard!");
                          }}
                        >
                          Copy Link
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => {
                            const randomCode = Math.random().toString(36).substring(2, 12);
                            setValue("slug", randomCode, { shouldDirty: true, shouldValidate: true });
                          }}
                        >
                          Generate Random Link
                        </Button>
                      ))}
                  </div>
                  {loading ? (
                    <Skeleton className="h-9 w-full rounded-lg" />
                  ) : (
                    <InputGroup
                      prepend={
                        <InputGroup.Addon>
                          <InputGroup.Text>{shortLinkBase}</InputGroup.Text>
                        </InputGroup.Addon>
                      }
                      control={controlProps => (
                        <Input
                          {...controlProps}
                          {...field}
                          className="inputWithLabel"
                          id="slug"
                          type="text"
                          placeholder="e.g., your-full-name"
                          onChange={e => {
                            field.onChange(removeSpecialCharacters(e.currentTarget.value));
                          }}
                        />
                      )}
                    />
                  )}
                  {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
                </Field>
              )}
            />

            {/* 3. Destination selector shown if playerHasPlayerCardAccess is true */}
            {playerHasPlayerCardAccess && (
              <Field>
                <Field.Label>Redirect Destination</Field.Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                  {/* Choice Card 1: Player's Website */}
                  {loadingPlayerCard ? (
                    <Skeleton className="h-[136px] w-full rounded-xl" />
                  ) : (
                    <Card
                      className={cn(
                        "cursor-pointer transition-all hover:bg-muted/50 border-2 flex flex-col justify-between p-4",
                        currentShortLinkSource === "CUSTOM_URL" || !currentShortLinkSource
                          ? "border-primary bg-primary/5"
                          : "border-border"
                      )}
                      onClick={() =>
                        setValue("shortLinkSource", "CUSTOM_URL", { shouldDirty: true, shouldValidate: true })
                      }
                    >
                      <div>
                        <div className="font-semibold text-base mb-1">Player's Website</div>
                        <p className="text-xs text-muted-foreground">
                          Direct visitors to the Player's Website{getTruncatedWebsiteLabel(currentWebAddress)}.
                        </p>
                      </div>
                      <div className="mt-4 flex items-center justify-between">
                        <span
                          className={cn(
                            "text-xs px-2 py-0.5 rounded-full font-medium",
                            currentShortLinkSource === "CUSTOM_URL" || !currentShortLinkSource
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground"
                          )}
                        >
                          {currentShortLinkSource === "CUSTOM_URL" || !currentShortLinkSource ? "Selected" : "Select"}
                        </span>
                      </div>
                    </Card>
                  )}

                  {/* Choice Card 2: Player Card */}
                  {loadingPlayerCard ? (
                    <Skeleton className="h-[136px] w-full rounded-xl" />
                  ) : (
                    <Card
                      className={cn(
                        "cursor-pointer transition-all hover:bg-muted/50 border-2 flex flex-col justify-between p-4",
                        currentShortLinkSource === "PLAYER_CARD" ? "border-primary bg-primary/5" : "border-border",
                        !playerCard && "opacity-75"
                      )}
                      onClick={() => {
                        if (playerCard) {
                          setValue("shortLinkSource", "PLAYER_CARD", { shouldDirty: true, shouldValidate: true });
                        } else {
                          toast.error("Please create a Player Card first");
                        }
                      }}
                    >
                      <div>
                        <div className="font-semibold text-base mb-1 flex items-center gap-2">
                          Player Card
                          {!playerCard && <TriangleAlert className="text-amber-500 h-4 w-4" />}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Direct visitors to the player's personalized social business card.
                        </p>
                      </div>
                      <div className="mt-4 flex flex-col gap-2">
                        {playerCard ? (
                          <div className="flex items-center justify-between">
                            <span
                              className={cn(
                                "text-xs px-2 py-0.5 rounded-full font-medium",
                                currentShortLinkSource === "PLAYER_CARD"
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted text-muted-foreground"
                              )}
                            >
                              {currentShortLinkSource === "PLAYER_CARD" ? "Selected" : "Select"}
                            </span>
                            <Link
                              to={`${
                                isPlayer ? URLS.player.playerCards : URLS.coach.playerCards
                              }?playerId=${playerId}&edit=true`}
                              className="text-xs text-primary hover:underline font-medium"
                              onClick={e => e.stopPropagation()}
                            >
                              Edit Card &rarr;
                            </Link>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            <span className="text-xs text-amber-600 font-medium">No card created yet</span>
                            <Link
                              to={`${
                                isPlayer ? URLS.player.playerCards : URLS.coach.playerCards
                              }?playerId=${playerId}&create=true`}
                              className="text-xs text-primary hover:underline font-semibold flex items-center gap-0.5"
                              onClick={e => e.stopPropagation()}
                            >
                              Create Player Card &rarr;
                            </Link>
                          </div>
                        )}
                      </div>
                    </Card>
                  )}
                </div>
              </Field>
            )}
          </div>
        </form>
      </FormCard.Content>
      <FormCard.Actions>
        <Button
          form="player-shortlink-form"
          type="submit"
          disabled={isSubmitting || loading}
          loading={isSubmitting || loading}
        >
          Save
        </Button>
      </FormCard.Actions>
    </FormCard>
  );
}
